"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Separator } from "@/components/ui/separator";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Facebook, Twitter, Instagram, Youtube, MapPin, Phone, Mail, ChevronRight } from "lucide-react";
const socialLinks = [{
  icon: Youtube,
  href: "#",
  label: "YouTube"
}, {
  icon: Facebook,
  href: "#",
  label: "Facebook"
}, {
  icon: Instagram,
  href: "#",
  label: "Instagram"
}, {
  icon: Twitter,
  href: "#",
  label: "Twitter"
}];
const paymentMethods = [{
  name: "Visa",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_visa_logo.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_visa_logo.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_visa_logo.png-wvc-sizes"
}, {
  name: "Mastercard",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_mastercard_logo.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_mastercard_logo.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_mastercard_logo.png-wvc-sizes"
}, {
  name: "American Express",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_amex_logo.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_amex_logo.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_amex_logo.png-wvc-sizes"
}, {
  name: "BKash",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_bkash_logo.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_bkash_logo.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_bkash_logo.png-wvc-sizes"
}, {
  name: "Nagad",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_nagad_logo.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_nagad_logo.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/payment_nagad_logo.png-wvc-sizes"
}];
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        {[1, 2, 3, 4, 5].map((_, i) => <div key={i} data-index={i} className="h-4 w-24 animate-pulse rounded-md bg-muted" />)}
      </div>;
  }
  if (menuItems.length === 0) {
    return null;
  }
  return <NavigationMenu viewport={false} className="max-w-full justify-start">
      <NavigationMenuList className="flex-col items-start gap-3 space-x-0">
        {menuItems.map((item, i) => {
        const pathMap: Record<string, string> = {
          "Home": "/",
          "Products": "/products",
          "About": "/about",
          "Solutions": "/solutions",
          "Service": "/service",
          "Blog": "/blog",
          "Contact": "/contact",
          "Category": "/category"
        };
        const targetPath = pathMap[item.label] || item.href || "#";
        return <NavigationMenuItem key={item.id} data-index={i} className="w-full">
              <NavigationMenuLink asChild className="w-full justify-start p-0 hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent">
                <Link to={targetPath} className="flex w-full items-start text-sm text-muted-foreground transition-colors hover:text-primary">
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function FooterInformation() {
  return <section data-section-id="252467" id="footer" className="bg-background text-foreground px-6 py-16 md:px-8 lg:py-20">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          <div className="flex flex-col gap-6">
            <Link to="/" className="inline-block w-fit">
              <WvcLogo className="h-8 w-auto text-foreground" />
            </Link>
            <p className="text-sm leading-relaxed text-muted-foreground font-default">
              Huayuan (Shenzhen) Intelligent Equipment Co., Ltd is a high‑tech enterprise specializing in the research, development, production, sales and service of intelligent sorting equipment, dedicated to providing efficient and precise intelligent sorting solutions to global customers.
            </p>
            <div className="flex items-center gap-3">
              {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <Link key={i} data-index={i} to={social.href} aria-label={social.label} className="flex size-10 items-center justify-center rounded-lg bg-muted text-muted-foreground transition-all duration-300 hover:bg-primary hover:text-primary-foreground hover:shadow-lg">
                    <Icon className="size-4" />
                  </Link>;
            })}
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="font-default text-lg font-bold text-foreground tracking-wider uppercase">
              Quick Links
            </h3>
            <MenuProvider menu_id="46">
              <FooterMenuConsumer />
            </MenuProvider>
            <NavigationMenu viewport={false} className="max-w-full justify-start mt-[-12px]">
              <NavigationMenuList className="flex-col items-start gap-3 space-x-0">
                <NavigationMenuItem className="w-full">
                  <NavigationMenuLink asChild className="w-full justify-start p-0 hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent">
                    <Link to="http://www.yayahei.cn/TtroubleTicket/Login" className="flex w-full items-start text-sm text-muted-foreground transition-colors hover:text-primary">
                      Work order
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          

          <div className="flex flex-col gap-6">
            <h3 className="font-default text-lg font-bold text-foreground tracking-wider uppercase">
              Contact
            </h3>
            <ul className="flex flex-col gap-4">
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Phone className="size-4 shrink-0 text-primary" />
                <span className="font-default">+86 13927478668</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Mail className="size-4 shrink-0 text-primary" />
                <span className="font-default">ivy@hysorter.com</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-muted-foreground">
                <MapPin className="mt-0.5 size-4 shrink-0 text-primary" />
                <span className="font-default">No. 7, Dongmao Road, Guangming District, Shenzhen</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center gap-6 md:flex-row md:justify-between">
          <div className="flex items-center gap-4">
            <span className="text-xs font-bold tracking-widest text-muted-foreground uppercase">
              Certified Partners
            </span>
            <div className="flex flex-wrap items-center gap-3">
              {paymentMethods.map((method, i) => <div key={i} data-index={i} className="flex h-10 w-16 items-center justify-center rounded-md border border-border bg-card p-2 shadow-sm transition-all duration-300 hover:border-primary hover:shadow-md">
                  <img src={method.src} data-wvc-srcset={method.srcset} data-wvc-sizes={method.sizes} alt={method.name} loading="lazy" className="max-h-full max-w-full object-contain opacity-100 grayscale-0" />
                </div>)}
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-4 py-2 text-xs font-bold tracking-widest text-muted-foreground uppercase">
            <span className="flex size-2 rounded-lg bg-primary animate-pulse"></span>
            System Status: Operational
          </div>
        </div>

        <Separator className="my-8 bg-border/50" />

        <div className="flex flex-col items-center justify-between gap-4 text-center md:flex-row md:text-left">
          <p className="text-xs font-default text-muted-foreground tracking-wide">
            © {new Date().getFullYear()} Huayuan. ALL RIGHTS RESERVED.
          </p>
        </div>
      </div>
    </section>;
}